import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Trophy, UsersThree, PersonSimpleRun, X, ArrowRight, CheckCircle } from "@phosphor-icons/react";

const SLIDES = [
  {
    icon: Trophy,
    color: "#CCFF00",
    tag: "Paso 1",
    title: "Marcador en vivo",
    body: "Crea retas, torneos y ligas. Registra sets en tiempo real y comparte el marcador con tu grupo por WhatsApp.",
    cta: "Continuar",
  },
  {
    icon: UsersThree,
    color: "#4AA6FF",
    tag: "Paso 2",
    title: "Comunidad y Cómic",
    body: "Publica en el muro social, comenta las publicaciones de tus amigos y disfruta cómics oficiales del administrador.",
    cta: "Continuar",
  },
  {
    icon: PersonSimpleRun,
    color: "#FFD24A",
    tag: "Paso 3",
    title: "DYD Run",
    body: "Registra tus carreras, comparte fotos y videos con la comunidad de corredores. Toggle público/privado según prefieras.",
    cta: "¡Empezar a jugar!",
  },
];

/**
 * OnboardingTour: mini-tour de 3 slides que se muestra la primera vez
 * que el usuario entra al dashboard tras registrarse.
 *
 * Props:
 *   - open: boolean
 *   - onClose(): cierra + marca onboarding_seen=true
 *   - variant?: 'first-run' | 'manual' — cambia el copy del botón cerrar
 */
export default function OnboardingTour({ open, onClose, variant = "first-run" }) {
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);

  if (!open) return null;

  const s = SLIDES[step];
  const Icon = s.icon;
  const isLast = step === SLIDES.length - 1;

  const finish = async () => {
    setSaving(true);
    try {
      await api.patch("/users/me/onboarding", { seen: true });
      if (variant === "manual") toast.success("Listo, ya conoces la plataforma");
    } catch { /* noop */ }
    finally {
      setSaving(false);
      setStep(0);
      onClose?.();
    }
  };

  const next = () => {
    if (isLast) return finish();
    setStep((n) => Math.min(SLIDES.length - 1, n + 1));
  };

  const skip = async () => {
    try { await api.patch("/users/me/onboarding", { seen: true }); } catch { /* noop */ }
    setStep(0);
    onClose?.();
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8"
      data-testid="onboarding-tour"
      role="dialog"
      aria-modal="true"
    >
      <button
        onClick={skip}
        className="absolute top-4 right-4 text-gray-400 hover:text-white h-9 w-9 flex items-center justify-center rounded-full bg-white/5 border border-white/10"
        aria-label="Cerrar"
        data-testid="onboarding-skip-btn"
      >
        <X size={18} />
      </button>

      <div className="w-full max-w-md space-y-6 animate-fade-in">
        {/* Progress dots */}
        <div className="flex items-center justify-center gap-2" data-testid="onboarding-dots">
          {SLIDES.map((_, i) => (
            <button
              key={i}
              onClick={() => setStep(i)}
              className={`h-1.5 rounded-full transition-all ${i === step ? "w-8 bg-[#CCFF00]" : "w-1.5 bg-white/25 hover:bg-white/50"}`}
              aria-label={`Ir al paso ${i + 1}`}
              data-testid={`onboarding-dot-${i}`}
            />
          ))}
        </div>

        {/* Icon */}
        <div className="flex justify-center">
          <div
            className="w-24 h-24 rounded-full flex items-center justify-center border-2"
            style={{ borderColor: s.color, boxShadow: `0 0 40px ${s.color}30` }}
          >
            <Icon size={44} weight="fill" style={{ color: s.color }} />
          </div>
        </div>

        {/* Copy */}
        <div className="text-center space-y-3">
          <p className="text-[10px] tracking-[0.3em] uppercase font-bold" style={{ color: s.color }}>{s.tag}</p>
          <h2 className="font-['Barlow_Condensed'] text-4xl sm:text-5xl font-black uppercase tracking-tighter leading-none" data-testid={`onboarding-title-${step}`}>
            {s.title}
          </h2>
          <p className="text-sm text-gray-300 max-w-sm mx-auto leading-relaxed">{s.body}</p>
        </div>

        {/* CTA */}
        <div className="flex flex-col gap-2 pt-4">
          <Button
            onClick={next}
            disabled={saving}
            className="w-full h-12 bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase tracking-wide"
            data-testid={`onboarding-next-btn-${step}`}
          >
            {isLast ? (
              <><CheckCircle size={18} weight="fill" className="mr-2" /> {s.cta}</>
            ) : (
              <>{s.cta} <ArrowRight size={16} className="ml-2" /></>
            )}
          </Button>
          {!isLast && (
            <button
              onClick={skip}
              className="text-[11px] uppercase tracking-widest text-gray-500 hover:text-white h-8"
              data-testid="onboarding-skip-link"
            >
              Saltar tour
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
